import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import cn from 'classnames';

import { ChipsSelectMenuPlacement, Modal } from '@components/common';
import ValueContent from '@components/common/GroupSelectKpi/ValueContent';

import { IFlatMoTreeNode } from '@interfaces/components/tree';
import { IGroupSelectItem, TChipsSelectMenuPlacementValues } from '@components/common/GroupSelect/GroupSelect.types';

import { DEFAULT_MODAL_HEIGHT } from '@constants/common';

import TreeGroupSelect from './TreeGroupSelect/TreeGroupSelect';
import { clearSearchText, sortNodesByCheckedCount } from './TreeGroupSelect/utils';
import { updateTreeNodeSelectionByNodeId } from './utils';

import styles from './TreeGroupSelectWrapper.module.scss';

const MAX_INVISIBLE_ITEMS_HEIGHT = 250;
const MODAL_WIDTH = '700px';

interface ITreeGroupSelectWrapper {
	data: IFlatMoTreeNode[];
	initData: IFlatMoTreeNode[];
	isDisabled?: boolean;
	isDisabledSubmit?: boolean;
	isIgnoreTreeChecked?: boolean;
	label?: string;
	maxSelectionNumber?: number | null;
	maxSelectionText?: string;
	onSubmit: (nodes: IFlatMoTreeNode[]) => void;
	searchPlaceholder?: string;
	selectedKpiNumber: number;
	setData: React.Dispatch<React.SetStateAction<IFlatMoTreeNode[]>> | undefined;
	setInitData: (nodes: IFlatMoTreeNode[]) => void;
	setIsDirty?: React.Dispatch<React.SetStateAction<boolean>>;
	setSelectedKpiNumber: React.Dispatch<React.SetStateAction<number>>;
	setTreeKpiIds: (ids: string[]) => void;
	title?: string;
	menuPlacement?: TChipsSelectMenuPlacementValues;
	isPortal?: boolean;
}

const TreeGroupSelectWrapper = ({
	data,
	initData,
	isDisabled,
	isDisabledSubmit,
	isIgnoreTreeChecked,
	label = 'Select KPI',
	maxSelectionNumber,
	maxSelectionText,
	onSubmit,
	searchPlaceholder = 'Search by KPI',
	selectedKpiNumber,
	setData,
	setInitData,
	setIsDirty,
	setSelectedKpiNumber,
	setTreeKpiIds,
	title = 'Select KPI',
	menuPlacement = ChipsSelectMenuPlacement.top,
	isPortal = false,
}: ITreeGroupSelectWrapper) => {
	const countRef = useRef(0);
	const containerRef = useRef<HTMLDivElement>(null);

	const [isOpenModal, setIsOpenModal] = useState(false);
	const [isDirtyForm, setIsDirtyForm] = useState(false);

	const selectedListOfOptions = useMemo(() => {
		return data.reduce<IGroupSelectItem[]>((acc, item) => {
			if (item.isChecked && item.dbId) {
				acc.push({ title: item.key, id: item.id, dbId: item.dbId });
			}

			return acc;
		}, []);
	}, [data]);

	const resetHandler = useCallback(() => {
		setData?.(initData);
		setIsDirtyForm(false);
	}, [setData, initData]);

	const handleCloseModal = useCallback(() => setIsOpenModal(false), []);
	const handleOpenModal = useCallback(() => setIsOpenModal(true), []);

	const resetAndClose = useCallback(() => {
		resetHandler();
		handleCloseModal();
	}, [resetHandler, handleCloseModal]);

	const handleSubmitData = useCallback(() => {
		const result = clearSearchText(sortNodesByCheckedCount(data));

		setData?.(result);
		onSubmit(result);
		handleCloseModal();
	}, [data, setData, onSubmit, handleCloseModal]);

	useEffect(() => {
		setIsDirtyForm(isOpenModal && selectedKpiNumber !== selectedListOfOptions.length);
	}, [selectedKpiNumber, selectedListOfOptions.length, isOpenModal]);

	useEffect(() => {
		if (setSelectedKpiNumber) {
			setSelectedKpiNumber(selectedListOfOptions.length);
		}
	}, [setSelectedKpiNumber, selectedListOfOptions?.length]);

	const isAllItemsSelected = selectedListOfOptions.length === data.filter((item) => item.dbId).length;

	const isDisableOpenSelectMenu = !data.length;

	countRef.current = countRef.current + 1;

	const removeAllOptions = useCallback(() => {
		setData?.((prev) => prev.map((item) => ({ ...item, isChecked: false })));
		setTreeKpiIds([]);

		setIsDirty?.(true);
	}, [setData, setIsDirty, setTreeKpiIds]);

	const removeOption = useCallback((
			ids: Array<string>,
			_categoryNames: string[],
		) => {
			const id = ids[0];

			const updatedData = updateTreeNodeSelectionByNodeId(data, setData, id);
			const sortedData = sortNodesByCheckedCount(updatedData);
			const kpiIds: string[] = selectedListOfOptions
				.filter((item) => item.id && !ids.includes(item.id))
				.map((item) => item.dbId || '');

			setData?.(sortedData);
			setInitData(sortedData);
			setTreeKpiIds(kpiIds);
			setIsDirty?.(true);
		}, [
			data,
			setData,
			setTreeKpiIds,
			selectedListOfOptions,
			setInitData,
			setIsDirty,
		],
	);

	return (
		<div
			ref={containerRef}
			className={styles.container}
			data-testid='group-select'
		>
			<ValueContent
				containerRef={containerRef}
				isAllItemsSelected={isAllItemsSelected}
				isPortal={isPortal}
				maxInvisibleItemsHeight={MAX_INVISIBLE_ITEMS_HEIGHT}
				menuPlacement={menuPlacement}
				readOnly={false}
				removeAllOptions={removeAllOptions}
				removeOptions={removeOption}
				selectedListOfOptions={selectedListOfOptions}
			/>
			<div className={styles.control}>
				<span
					className={cn(styles.button, {
						buttonDisabled: isDisableOpenSelectMenu,
					})}
					data-testid='group-select-button'
					onClick={handleOpenModal}
				>
					{label}
				</span>
				&nbsp;
				{!!selectedListOfOptions.length && (
					<div data-testid='select-count'>({isAllItemsSelected ? 'all' : selectedListOfOptions.length})</div>
				)}
			</div>
			<Modal
				height={DEFAULT_MODAL_HEIGHT}
				isConfirmDialogNeededOnClose={!isDisabledSubmit}
				isDirtyForm={isDirtyForm && !isDisabledSubmit}
				isOpen={isOpenModal}
				onClose={resetAndClose}
				title={title}
				width={MODAL_WIDTH}
			>
				<TreeGroupSelect
					data={data}
					isDisabled={isDisabled}
					isDisabledSubmit={isDisabledSubmit}
					isIgnoreTreeChecked={isIgnoreTreeChecked}
					maxSelectionNumber={maxSelectionNumber}
					maxSelectionText={maxSelectionText}
					onSubmit={handleSubmitData}
					resetHandler={resetHandler}
					searchPlaceholder={searchPlaceholder}
					setData={setData}
				/>
			</Modal>
		</div>
	);
};

export default memo(TreeGroupSelectWrapper);
